import type { eventWithTime } from '@carboncopyco/rrweb-types';
import type { RRWebEvent, TourFrame } from '@carboncopyco/types';
import { FrameType } from '@carboncopyco/types';
import { useCallback, useEffect, useMemo } from 'react';
import { setIsGeneratingReplayFrameChunks } from '../../store';
import { EventIndex, type frameSeparation } from '../../utils/eventIndex';
import { preloadImage } from '../../utils/preloadImage';
import { getFrameIndetifier } from '../../utils/replayer';
import { useTourDispatch } from '../store';

type ReturnType = {
  frameChunks: frameSeparation[];
  getCurrentFrameChunk: (activeFrame: TourFrame) => frameSeparation;
};

export const useFrameChunks = (
  frames: TourFrame[],
  events: eventWithTime[],
  uuid: string,
  fileServiceApiToken: string
): ReturnType => {
  const dispatch = useTourDispatch();
  const chunks = useMemo(() => {
    const eventIndex = new EventIndex(events as RRWebEvent[]);
    const chunks = eventIndex.generateReplayFrameChunks();

    let chunkIndex = 0;
    if (chunks.length > 0) {
      frames.forEach((frame) => {
        const chunk = chunks[chunkIndex];
        if (chunk && frame && frame.frameDataUrl) {
          chunk.frameReference = getFrameIndetifier(frame);
          chunkIndex++;
        } else {
          if (chunk && !!chunk.frameReference) {
            // reset stale reference
            chunk.frameReference = undefined;
          }
        }
      });
    }
    return chunks;
  }, [events]);

  const frameChunks = useMemo(() => {
    // Preloading media
    frames.forEach((frame) => {
      frame.steps?.forEach((step) => {
        step.mediaContent?.forEach((media) => {
          if (media.type === 'image') {
            preloadImage(`${media.url}?token=${fileServiceApiToken}`);
          }

          if (media.type === 'video') {
            fetch(`${media.url}?token=${fileServiceApiToken}`, {
              redirect: 'follow',
            });
          }
        });
      });
    });

    // update indexes to all chunk
    chunks.forEach((chunk, _index) => {
      const frame = frames.find(
        (frame) => getFrameIndetifier(frame) === chunk.frameReference
      );
      if (frame) {
        const screenshotUrl =
          frame.frameType === FrameType.screenshot
            ? frame.frameScreenshotUrl
            : undefined;

        chunk.screenshotUrl = screenshotUrl;
      }
    });

    const preloadScreenshots = async () => {
      const framesWithScreenshots = frames.filter(
        (frame) =>
          frame.frameType === FrameType.screenshot && frame.frameScreenshotUrl
      );
      for (const frame of framesWithScreenshots) {
        await preloadImage(
          `${frame.frameScreenshotUrl}?token=${fileServiceApiToken}`
        );
      }
    };

    preloadScreenshots();

    return chunks;
  }, [chunks]);

  const getCurrentFrameChunk = useCallback(
    (activeFrame: TourFrame) => {
      if (activeFrame?.frameType === FrameType.screenshot) {
        return frameChunks[0];
      }
      const frameIndetifier = getFrameIndetifier(activeFrame);

      return (
        frameChunks.find(
          (frameChunk) => frameChunk?.frameReference === frameIndetifier
        ) || frameChunks[0]
      );
    },
    [frameChunks]
  );

  useEffect(() => {
    if (frameChunks?.length === 0) {
      return;
    }
    dispatch(
      setIsGeneratingReplayFrameChunks({
        uuid,
        isGenerating: false,
      })
    );
  }, [events, frames]);

  return { frameChunks, getCurrentFrameChunk };
};
